import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createContact,
  updateContact,
  fetchContacts,
} from "../../api/customer/contact";
import produce from "immer";
import { normalizeError, hasMeaningfulErrors } from "../../operationFeedback";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  system: {},
  data: [],
  currentCount: 0,
  totalCount: 0,
  newCount: 0,
};

export const initialController = {
  filters: [],
  page: 0,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  view: "all",
};

function rejectNormalized(rejectWithValue, err, operationKey) {
  return rejectWithValue(normalizeError(err, { operationKey }));
}

function isHttpFailureStatus(status) {
  const n = Number(status);
  return Number.isFinite(n) && n >= 400;
}

const GENERIC_SAFE =
  /^(En uventet feil oppstod\.|Handlingen kunne ikke fullføres\.)$/;

function rejectedErrors(action, fallbackNb) {
  const msg = action.payload?.safeMessage;
  if (typeof msg === "string" && msg.trim() && !GENERIC_SAFE.test(msg)) {
    return [msg];
  }
  return [fallbackNb];
}

export const updateContactAsync = createAsyncThunk(
  "contacts/updateContactAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await updateContact(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "update_contact");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "update_contact");
    }
  }
);

export const createContactAsync = createAsyncThunk(
  "contacts/createContactAsync",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await createContact(payload);
      if (response?.error || hasMeaningfulErrors(response?.errors)) {
        return rejectNormalized(rejectWithValue, response, "create_contact");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "create_contact");
    }
  }
);

export const getContactsAsync = createAsyncThunk(
  "cases/getContactsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    try {
      const response = await fetchContacts(controller);
      const status = response?.response?.status ?? response?.status;
      if (
        isHttpFailureStatus(status) ||
        hasMeaningfulErrors(response?.errors)
      ) {
        return rejectNormalized(rejectWithValue, response, "list_contacts");
      }
      return response;
    } catch (err) {
      return rejectNormalized(rejectWithValue, err, "list_contacts");
    }
  }
);

const systemSlice = createSlice({
  name: "contacts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getContactsAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })

      .addCase(getContactsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        // Infinite scroll: append page results instead of replacing
        if (action.meta?.arg?.append) {
          const existing = new Set((state.data || []).map((d) => d.id));
          const incoming = (action.payload.data || []).filter(
            (d) => !existing.has(d.id)
          );
          state.data = [...(state.data || []), ...incoming];
        } else {
          state.data = action.payload.data;
        }
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
        state.newCount = action.payload.newCount;
      })

      .addCase(getContactsAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke hente kontaktene.");
      })
      .addCase(updateContactAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(updateContactAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          const id = action.payload?.id;
          const index = draftState.data.findIndex((type) => type.id === id);
          if (index >= 0) {
            draftState.data[index] = action.payload;
          }
          draftState.isLoading = false;
        });
      })
      .addCase(updateContactAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke oppdatere kontakten.");
      })
      .addCase(createContactAsync.pending, (state) => {
        state.isLoading = true;
        state.error = false;
        state.errors = null;
      })
      .addCase(createContactAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          if (action.payload?.id) {
            draftState.data.push(action.payload);
          }
          draftState.isLoading = false;
        });
      })
      .addCase(createContactAsync.rejected, (state, action) => {
        state.isLoading = false;
        state.error = true;
        state.errors = rejectedErrors(action, "Kunne ikke opprette kontakten.");
      });
  },
});

export const { reducer } = systemSlice;

export default systemSlice;
